import enTranslations from '../locales/en.json';

type DeepPartial<T> = T extends object ? {
  [P in keyof T]?: DeepPartial<T[P]>;
} : T;

type TranslationSource = DeepPartial<typeof enTranslations> & { slogan?: string };

/**
 * Safely access nested translation values with fallback to English
 * @param translations - Current language translations (may be incomplete)
 * @param path - Dot-separated path to the translation key (e.g., 'participants.microphoneMutedByAdmin')
 * @returns The translation string, falling back to English if not found
 */
export function getTranslation(translations: TranslationSource, path: string): string {
  const keys = path.split('.');

  // Try to get from current language
  let value: any = translations;
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key as keyof typeof value];
    } else {
      value = undefined;
      break;
    }
  }

  if (typeof value === 'string') {
    return value;
  }

  // Fallback to English
  let fallback: any = enTranslations;
  for (const key of keys) {
    if (fallback && typeof fallback === 'object' && key in fallback) {
      fallback = fallback[key as keyof typeof fallback];
    } else {
      return path; // Return the path itself as last resort
    }
  }

  return typeof fallback === 'string' ? fallback : path;
}

/**
 * Picks the plural form matching `count` for the given language.
 * `forms` is a CLDR-keyed map ("one" / "few" / "many" / "other"); a plain string is
 * returned as is, so locales without plural agreement can keep a single value.
 */
export function selectPluralForm(
  forms: string | Record<string, string>,
  count: number,
  language: string
): string {
  if (typeof forms === 'string') {
    return forms;
  }

  let category = 'other';
  try {
    category = new Intl.PluralRules(language).select(count);
  } catch {
    // Unsupported/invalid language tag — fall back to English rules
    category = new Intl.PluralRules('en').select(count);
  }

  // `in` goes through the proxy's target, so a missing form is detected instead of
  // silently resolving to createSafeTranslations' key-name fallback.
  if (category in forms) {
    return forms[category];
  }

  return 'other' in forms ? forms.other : '';
}

/**
 * Create a typed accessor that always returns a string (never undefined)
 * This ensures you don't need fallback strings at the call site
 */
export function createSafeTranslations(translations: TranslationSource): typeof enTranslations & { slogan?: string } {
  return new Proxy(translations, {
    get(target, prop: string) {
      const value = target[prop as keyof typeof target];

      // If it's a nested object, wrap it recursively
      if (value && typeof value === 'object') {
        return createSafeTranslations(value as any);
      }

      // If it's a string, return it
      if (typeof value === 'string') {
        return value;
      }

      // Otherwise, try to get from English
      const enValue = (enTranslations as any)[prop];
      return enValue || prop;
    }
  }) as typeof enTranslations & { slogan?: string };
}
